import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, User, Tag, Clock, ChevronRight,
  Share2, Linkedin, Facebook, ArrowRight
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { insights } from '../data/mock';

const InsightArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useFallback, setUseFallback] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  // Fetch article from API or fallback to mock data
  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      
      try {
        // Try CMS API first (new endpoint)
        const response = await fetch(`${API_URL}/api/content/insights/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setArticle(data);
          setUseFallback(false);
        } else {
          // Fallback to mock data
          const mockArticle = insights.find(a => a.slug === slug || a.id === slug);
          if (mockArticle) {
            setArticle({
              ...mockArticle,
              content: mockArticle.excerpt + '\n\n' + generateMockContent(mockArticle),
              author: 'ASAP Capital Research Team'
            });
            setUseFallback(true);
          } else {
            navigate('/insights');
          }
        }
      } catch (err) {
        // Fallback to mock data on error
        const mockArticle = insights.find(a => a.slug === slug || a.id === slug);
        if (mockArticle) {
          setArticle({
            ...mockArticle,
            content: mockArticle.excerpt + '\n\n' + generateMockContent(mockArticle),
            author: 'ASAP Capital Research Team'
          });
          setUseFallback(true);
        } else {
          navigate('/insights');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug, API_URL, navigate]);

  // Get related articles
  useEffect(() => {
    if (article) {
      const related = insights
        .filter(a => a.slug !== slug && a.id !== slug)
        .filter(a => a.category === article.category || a.tags?.some(t => article.tags?.includes(t)))
        .slice(0, 3);
      setRelatedArticles(related);
    }
  }, [article, slug]);

  // Generate mock content for demo purposes
  const generateMockContent = (article) => {
    return `
## Overview

${article.excerpt}

The Houston real estate market continues to demonstrate strong fundamentals that support our investment thesis. As a leading private equity real estate firm focused on value-add opportunities, ASAP Capital remains committed to identifying and capitalizing on emerging trends within the Greater Houston metropolitan area.

## Market Analysis

Our research team has identified several key factors driving current market conditions:

- **Population Growth**: Houston continues to attract residents from across the country, drawn by employment opportunities and affordable cost of living
- **Economic Diversification**: Beyond energy, Houston's economy has expanded into healthcare, technology, and manufacturing
- **Infrastructure Investment**: Ongoing infrastructure improvements enhance property values and accessibility

## Investment Implications

These market dynamics create compelling opportunities for disciplined investors:

1. **Value-Add Multifamily**: Properties in transitioning neighborhoods offer significant upside potential through strategic renovations
2. **Commercial Repositioning**: Well-located office and retail assets can be repositioned to capture changing tenant preferences
3. **Portfolio Diversification**: A balanced approach across asset classes provides risk-adjusted returns

## Conclusion

ASAP Capital's vertically integrated platform positions us to capitalize on these opportunities while managing risk through hands-on asset management and local market expertise.

*For more information about our investment strategy and current opportunities, please contact our investor relations team.*
    `;
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  // Share URL
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{article.title} | ASAP Capital Insights</title>
        <meta name="description" content={article.excerpt} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:image" content={article.featured_image || article.image || article.image_url} />
        <link rel="canonical" href={`/insights/${slug}`} />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* Hero */}
          <section className="relative pt-32 pb-12 lg:pt-40 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            
            <div className="relative z-10 max-w-[900px] mx-auto px-6 lg:px-12">
              {/* Breadcrumb */}
              <motion.div 
                className="flex items-center gap-2 text-sm mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Link to="/insights" className="text-white/50 hover:text-[#D4AF37] transition-colors">
                  Insights
                </Link>
                <ChevronRight className="h-4 w-4 text-white/30" />
                <span className="text-white/70 truncate">{article.title}</span>
              </motion.div>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                {/* Category & Date */}
                <motion.div className="flex items-center gap-4 mb-4" variants={itemVariants}>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20">
                    {article.category}
                  </span>
                  <span className="text-white/50 text-sm flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatDate(article.publish_date || article.date || article.created_at)}
                  </span>
                </motion.div>

                {/* Title */}
                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6"
                  variants={itemVariants}
                >
                  {article.title}
                </motion.h1>

                {/* Excerpt */}
                <motion.p 
                  className="text-lg text-white/60 mb-6"
                  variants={itemVariants}
                >
                  {article.excerpt}
                </motion.p>

                {/* Author & Read Time */}
                <motion.div 
                  className="flex items-center gap-6 text-white/50 text-sm"
                  variants={itemVariants}
                >
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {article.author || 'ASAP Capital'}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {article.readTime || '5 min read'}
                  </span>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* Featured Image */}
          {(article.featured_image || article.image || article.image_url) && (
            <section className="pb-12">
              <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl overflow-hidden border border-white/10"
                >
                  <img 
                    src={article.featured_image || article.image || article.image_url}
                    alt={article.title}
                    className="w-full aspect-[2/1] object-cover"
                  />
                </motion.div>
              </div>
            </section>
          )}

          {/* Content */}
          <section className="py-12">
            <div className="max-w-[800px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="prose prose-invert prose-lg max-w-none
                  prose-headings:text-white prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-p:text-white/70 prose-p:leading-relaxed
                  prose-li:text-white/70
                  prose-strong:text-white
                  prose-a:text-[#D4AF37] prose-a:no-underline hover:prose-a:underline"
                dangerouslySetInnerHTML={{ __html: article.content?.replace(/\n/g, '<br/>') || '' }}
              />

              {/* Tags */}
              {article.tags?.length > 0 && (
                <div className="mt-12 pt-8 border-t border-white/10">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Tag className="h-4 w-4 text-white/40" />
                    {article.tags.map((tag, i) => (
                      <span key={i} className="px-3 py-1 rounded-full bg-white/5 text-white/60 text-sm border border-white/10">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="flex items-center gap-4">
                  <span className="text-white/50 text-sm flex items-center gap-2">
                    <Share2 className="h-4 w-4" />
                    Share
                  </span>
                  <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/5 text-white/50 hover:text-[#D4AF37] hover:bg-white/10 transition-colors">
                    <Linkedin className="h-5 w-5" />
                  </a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/5 text-white/50 hover:text-[#D4AF37] hover:bg-white/10 transition-colors">
                    <Facebook className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="py-16 bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
              <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
                <h2 className="text-2xl font-bold text-white mb-8">Related Insights</h2>
                
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedArticles.map((related) => (
                    <Link 
                      key={related.id}
                      to={`/insights/${related.slug || related.id}`}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10 overflow-hidden hover:border-[#D4AF37]/30 transition-all group"
                    >
                      {related.image && (
                        <div className="aspect-[16/10] overflow-hidden">
                          <img 
                            src={related.image} 
                            alt={related.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      <div className="p-5">
                        <span className="text-[#D4AF37] text-sm">{related.category}</span>
                        <h3 className="text-white font-semibold mt-1 group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                          {related.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="py-16">
            <div className="max-w-[900px] mx-auto px-6 lg:px-12 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">Interested in Learning More?</h2>
              <p className="text-white/50 mb-6">
                Schedule a consultation with our team to discuss investment opportunities.
              </p>
              <div className="flex gap-4 justify-center">
                <a to="/contact" >
                  <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-8">
                    Schedule Consultation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <Link to="/insights">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    All Insights
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default InsightArticlePage;
